import { useState } from "react";
import { LockKey, UserCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

export default function Account() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Account</div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">My Account</h1>
      </div>

      <Card className="p-6 border-border" data-testid="account-profile">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/5 flex items-center justify-center"><UserCircle size={30} weight="duotone" className="text-primary" /></div>
          <div>
            <div className="font-display font-semibold text-lg text-primary">{user?.name}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
            <div className="text-xs text-accent mt-0.5 flex items-center gap-1"><ShieldCheck size={13} /> {ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-1"><LockKey size={20} weight="duotone" className="text-accent" /><h3 className="font-display text-xl font-semibold text-primary">Change Password</h3></div>
        <p className="text-xs text-muted-foreground mb-5">Use at least 8 characters with a letter and a number. Changing your password signs out your other devices.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5"><Label>Current Password</Label><Input type="password" name="current-password" autoComplete="current-password" required value={form.current_password} onChange={set("current_password")} data-testid="current-password" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>New Password</Label><Input type="password" name="new-password" autoComplete="new-password" required value={form.new_password} onChange={set("new_password")} data-testid="new-password" /></div>
            <div className="space-y-1.5"><Label>Confirm New Password</Label><Input type="password" name="confirm-new-password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} data-testid="confirm-password" /></div>
          </div>
          <Button type="submit" disabled={loading} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="change-password-btn">{loading ? "Updating…" : "Update Password"}</Button>
        </form>
      </Card>
    </div>
  );
}
